import { createAction, props } from '@ngrx/store';
import { AppTemplate } from '../../models/app-template.class';
import { AssetData } from '../../models/assetdata.class';
import { AssetService } from '../../models/assetservice.class';
import { Message } from '../../models/record/message.class';
import { ReconciliationConfiguration } from '../../models/reconciliation-configuration.class';

import { AppsState } from '../states';

enum AppsActionTypes {
    GET_HELP_DOCUMENTS = '[APPS] Get Help Documents',
    SET_HELP_DOCUMENTS = '[APPS] Set Help Documents',
    GET_BRIDGE_NODES = '[APPS] Get Bridge Nodes',
    SET_BRIDGE_NODES = '[APPS] Set Bridge Nodes',
    GET_SUBSCRIBED_SERVICES = '[APPS] Get Subscribed Services',
    SET_SUBSCRIBED_SERVICES = '[APPS] Set Subscribed Services',
    GET_METADATA = '[APPS] Get Metadata',
    SET_METADATA = '[APPS] Set Metadata',
    GET_METADATA_PERMISSIONS = '[APPS] Get Metadata Permissions',
    SET_METADATA_PERMISSIONS = '[APPS] Set Metadata Permissions',
    GET_RECON_CONFIGURATION = '[APPS] Get Recon Configuration',
    SET_RECON_CONFIGURATION = '[APPS] Set Recon Configuration',
    SET_LOADING = '[APPS] Set Loading',
    CLEAR_ALL_APPS_LOADINGS = '[APPS] Clear all loadings',
    SET_SERVICE_ID = '[APPS] Set Service ID',
    SET_BRIDGE_SERVICE_ID = '[APPS] Set Bridge Service ID',
    SET_PACKAGE_SERVICE_ID = '[APPS] Set Package Service ID',
    SET_REST_API_NAME = '[APPS] Set Rest API Name',
    SET_BRIDGE_REST_API_NAME = '[APPS] Set Bridge Rest API Name',
    SET_PACKAGE_REST_API_NAME = '[APPS] Set Package Rest API Name',
    SET_CATEGORY = '[APPS] Set Category',
    SET_ENTITY = '[APPS] Set Entity',
    ROUTE_CATEGORY = '[APPS] Route Category',
    GET_TEMPLATE = '[APPS] Get Template',
    SET_TEMPLATE = '[APPS] Set Template',
    TRIGGER_SUBMIT_TEMPLATE = '[APPS] Trigger Submit Template',
    SUBMIT_TEMPLATE = '[APPS] Submit Template',
    DELETE_TEMPLATE = '[APPS] Delete Template',
    GET_APP_CONFIGURATOR = '[APPS] Get App Configurator',
    SET_APP_CONFIGURATOR = '[APPS] Set App Configurator',
    GET_ASSET_WITH_LATEST_VERSION = '[APPS] Get Asset With Latest Version',
    SET_ASSET_WITH_LATEST_VERSION = '[APPS] Set Asset With Latest Version',
    GET_ASSETS_FOR_SUBSCRIPTION_APPROVAL_FILTERS = '[APPS] Get Assets For Subscription Approval Filters',
    SET_ASSETS_FOR_SUBSCRIPTION_APPROVAL_FILTERS = '[APPS] Set Assets For Subscription Approval Filters',
    GET_PACKAGE_APPS = '[APPS] Get Package Apps',
    SET_PACKAGE_APPS = '[APPS] Set Package Apps',
    SET_TAXONOMY_APP = '[APPS] Set Taxonomy App',
    GET_APP_DETAILS_THROUGH_API = '[APPS] Get App Details Through API',
    SET_APP_DETAILS_THROUGH_API = '[APPS] Set App Details Through API',
    SET_APP_TAGS = '[APPS] Set App Tags',
    GET_APP_THROUGH_APP_NAME = '[APPS] Get App Through App Name',
}

export const GetAppHelpDocumentation = createAction(
    AppsActionTypes.GET_HELP_DOCUMENTS,
    props<{
        serviceId: string;
        bridgeServiceId?: string;
        bridgeNodeId?: string;
    }>()
);

export const SetAppHelpDocumentation = createAction(
    AppsActionTypes.SET_HELP_DOCUMENTS,
    props<{
        serviceId: string;
        helpDocuments: AppsState['appsHelpDocumentsByServiceId'][''];
        bridgeNodeId: string;
        bridgeServiceId?: string;
    }>()
);

export const GetAppBridgeNodes = createAction(
    AppsActionTypes.GET_BRIDGE_NODES,
    props<{
        serviceId?: string;
    }>()
);

export const SetAppBridgeNodes = createAction(
    AppsActionTypes.SET_BRIDGE_NODES,
    props<{
        bridgeNodes: AppsState['bridgeServices'][''];
        assetId: string;
        serviceId?: string;
    }>()
);

export const GetSubscribedApps = createAction(AppsActionTypes.GET_SUBSCRIBED_SERVICES);

export const SetSubscribedApps = createAction(
    AppsActionTypes.SET_SUBSCRIBED_SERVICES,
    props<{
        services: AssetService[];
        organizationId: string;
    }>()
);

export const GetAppMetaData = createAction(
    AppsActionTypes.GET_METADATA,
    props<{
        serviceId: string;
        assetId?: string;
        bridgeServiceId?: string;
        noAlerts?: boolean;
        guest?: boolean;
        forceFetch?: boolean;
    }>()
);

export const SetAppMetaData = createAction(
    AppsActionTypes.SET_METADATA,
    props<{
        serviceId: string;
        metaData: AssetData;
    }>()
);

export const GetAppMetaDataPermissions = createAction(
    AppsActionTypes.GET_METADATA_PERMISSIONS,
    props<{
        serviceId: string;
        assetType: string;
        assetName?: string;
        noAlerts?: boolean;
    }>()
);

export const SetAppMetaDataPermissions = createAction(
    AppsActionTypes.SET_METADATA_PERMISSIONS,
    props<{
        serviceId: string;
        metaDataPermissions: any;
    }>()
);

export const GetReconConfiguration = createAction(
    AppsActionTypes.GET_RECON_CONFIGURATION,
    props<{
        serviceId: string;
        apiName: string;
        noAlerts: boolean;
    }>()
);

export const SetReconConfiguration = createAction(
    AppsActionTypes.SET_RECON_CONFIGURATION,
    props<{
        serviceId: string;
        configuration: ReconciliationConfiguration;
    }>()
);

export const SetAppLoading = createAction(
    AppsActionTypes.SET_LOADING,
    props<{
        serviceId: string;
        loading: boolean;
        property?: 'meta' | 'template' | 'reconConfig' | 'bridgeNodes' | 'configurator' | 'version';
    }>()
);

export const ClearAllAppsLoadings = createAction(AppsActionTypes.CLEAR_ALL_APPS_LOADINGS);

export const SetSelectedAppRestApiName = createAction(
    AppsActionTypes.SET_REST_API_NAME,
    props<{
        restApiName: string;
    }>()
);

export const SetSelectedBridgeRestApiName = createAction(
    AppsActionTypes.SET_BRIDGE_REST_API_NAME,
    props<{
        restApiName: string;
    }>()
);

export const SetSelectedPackageRestApiName = createAction(
    AppsActionTypes.SET_PACKAGE_REST_API_NAME,
    props<{
        restApiName: string;
    }>()
);

export const SetSelectedAppServiceId = createAction(
    AppsActionTypes.SET_SERVICE_ID,
    props<{
        serviceId: string;
    }>()
);

export const SetSelectedBridgeServiceId = createAction(
    AppsActionTypes.SET_BRIDGE_SERVICE_ID,
    props<{
        serviceId: string;
    }>()
);

export const SetSelectedPackageServiceId = createAction(
    AppsActionTypes.SET_PACKAGE_SERVICE_ID,
    props<{
        serviceId: string;
    }>()
);

export const RouteSelectedAppCategory = createAction(
    AppsActionTypes.ROUTE_CATEGORY,
    props<{
        category: AppsState['selectedApp']['category'];
    }>()
);

export const SetSelectedAppCategory = createAction(
    AppsActionTypes.SET_CATEGORY,
    props<{
        category: AppsState['selectedApp']['category'];
    }>()
);

export const SetSelectedAppEntity = createAction(
    AppsActionTypes.SET_ENTITY,
    props<{
        entityId: string;
    }>()
);

export const GetAppTemplate = createAction(
    AppsActionTypes.GET_TEMPLATE,
    props<{
        serviceId: string;
        noAlerts: boolean;
        guest?: boolean;
        forceFetch?: boolean;
    }>()
);

export const SetAppTemplate = createAction(
    AppsActionTypes.SET_TEMPLATE,
    props<{
        organizationId: string;
        serviceId: string;
        template: AppsState['appsTemplate']['orgId']['serviceId'];
    }>()
);

export const AppTriggerSubmitTemplate = createAction(
    AppsActionTypes.TRIGGER_SUBMIT_TEMPLATE,
    props<{
        trigger: boolean;
    }>()
);

export const AppSubmitTemplate = createAction(
    AppsActionTypes.SUBMIT_TEMPLATE,
    props<{
        details: {
            id: string;
            label: string;
            value: string;
            readOnly: boolean;
            required?: boolean;
            message?: Message;
        }[];
        entitiesOrder: AppTemplate['entities'];
        entityDetails: {
            id: string;
            label: string;
            value: string | boolean;
            readOnly: boolean;
            required?: boolean;
            message?: Message;
        }[];
        fieldDetails: {
            fieldId: string;
            fieldOrder: number;
            details: {
                id: string;
                value: any;
            }[];
        }[];
        workflowDetails?: {
            uid: string;
            displayName: string;
            userActions?: { name: string; displayName: string; order?: number }[];
        }[];
    }>()
);

export const AppDeleteTemplate = createAction(AppsActionTypes.DELETE_TEMPLATE);

export const GetAppConfigurator = createAction(
    AppsActionTypes.GET_APP_CONFIGURATOR,
    props<{
        serviceId: string;
        assetId?: string;
        bridgeServiceId?: string;
        noAlerts?: boolean;
        forceFetch?: boolean;
        guest?: boolean;
    }>()
);

export const SetAppConfigurator = createAction(
    AppsActionTypes.SET_APP_CONFIGURATOR,
    props<{
        serviceId: string;
        assetMetaUID: string;
        configurator: any;
    }>()
);

export const GetAssetWithLatestVersion = createAction(
    AppsActionTypes.GET_ASSET_WITH_LATEST_VERSION,
    props<{
        serviceId: string;
    }>()
);
export const SetAssetWithLatestVersion = createAction(
    AppsActionTypes.SET_ASSET_WITH_LATEST_VERSION,
    props<{
        serviceId: string;
        asset: any;
    }>()
);

export const GetAssetsForSubscriptionApprovalFilters = createAction(AppsActionTypes.GET_ASSETS_FOR_SUBSCRIPTION_APPROVAL_FILTERS);

export const SetAssetsForSubscriptionApprovalFilters = createAction(
    AppsActionTypes.SET_ASSETS_FOR_SUBSCRIPTION_APPROVAL_FILTERS,
    props<{
        assets: {
            serviceId: string;
            restApiName: string;
            displayName: string;
            organizationId: string;
        }[];
    }>()
);

export const GetPackageApps = createAction(
    AppsActionTypes.GET_PACKAGE_APPS,
    props<{
        serviceId: string;
    }>()
);

export const SetPackageGroupApps = createAction(
    AppsActionTypes.SET_PACKAGE_APPS,
    props<{
        serviceId: string;
        packagedApps: AssetService[];
    }>()
);

export const SetTaxonomyApp = createAction(
    AppsActionTypes.SET_TAXONOMY_APP,
    props<{
        taxonomyApp: AssetService;
    }>()
);

export const GetAppDetailsThroughApi = createAction(
    AppsActionTypes.GET_APP_DETAILS_THROUGH_API,
    props<{
        apiName: string;
    }>()
);

export const SetAppDetailsThroughApi = createAction(
    AppsActionTypes.SET_APP_DETAILS_THROUGH_API,
    props<{
        apiName: string;
        appDetails: AssetService;
        organizationId: string;
    }>()
);

export const SetAppTags = createAction(
    AppsActionTypes.SET_APP_TAGS,
    props<{
        organizationId: string;
        serviceId: string;
        tags: AssetService['tags'];
    }>()
);

export const GetAppThroughAppName = createAction(
    AppsActionTypes.GET_APP_THROUGH_APP_NAME,
    props<{
        appName: string;
    }>()
);
